import type {LogEntry} from '../types/session'
import {isPermissionRequestArguments} from '../types/session'
import {extractPromptFromEntry, type NormalizedPrompt} from '../components/session-logs/prompt-log-utils'

const MAX_PREVIEW_VALUE_CHARS = 200

export interface PermissionLabelInfo {
  isCommand: boolean
  isWrite: boolean
  isMcp: boolean
  label: string
}

/** Derive display label and kind flags from permission request fields. */
export function getPermissionLabel(kind: string, toolName?: string, serverName?: string): PermissionLabelInfo {
  const isCommand = kind === 'commands' || kind === 'shell'
  const isWrite = kind === 'write'
  const isMcp = kind === 'mcp'

  const label = isCommand
    ? 'Run command'
    : isWrite
      ? 'Edit file'
      : isMcp
        ? `MCP tool: ${serverName ? `${serverName}/` : ''}${toolName ?? 'unknown'}`
        : `Permission: ${toolName ?? kind}`

  return {isCommand, isWrite, isMcp, label}
}

/**
 * Format an MCP tool call's args as a multi-line `key: value` preview.
 * Strings render verbatim (truncated past MAX_PREVIEW_VALUE_CHARS); other
 * shapes fall back to JSON.stringify so nested objects still render
 * something useful. Returns undefined when there's nothing meaningful to
 * preview, so the caller can collapse the preview slot entirely.
 */
export function formatMcpArgsForPreview(mcpArgs: Record<string, unknown> | undefined): string | undefined {
  if (!mcpArgs) return undefined
  const entries = Object.entries(mcpArgs)
  if (entries.length === 0) return undefined

  const lines: string[] = []
  for (const [key, value] of entries) {
    if (value === undefined || value === null) continue
    let str: string
    if (typeof value === 'string') {
      str = value
    } else {
      try {
        str = JSON.stringify(value)
      } catch {
        str = String(value)
      }
    }
    if (str.length > MAX_PREVIEW_VALUE_CHARS) {
      str = `${str.slice(0, MAX_PREVIEW_VALUE_CHARS)}…`
    }
    lines.push(`${key}: ${str}`)
  }
  return lines.length > 0 ? lines.join('\n') : undefined
}

/**
 * For a permission_response with `approved: true` and `scope: "session"`
 * on an MCP prompt, find every other pending permission_request in the
 * logs sharing the same (serverName, toolName) so the caller can
 * optimistically resolve them too. Mirrors the runtime's
 * `resumePendingPermissions` fan-out so the FIFO advances past sibling
 * prompts immediately on click.
 *
 * Returns an empty array for non-MCP prompts, scope="once" responses,
 * denials, or when there are no sibling prompts in the logs.
 */
export function findSessionFanoutTargets(
  pendingPrompt: NormalizedPrompt,
  response: unknown,
  logs: LogEntry[] | undefined,
  triggeringToolCallId: string,
): Array<{toolCallId: string}> {
  if (!logs?.length) return []
  if (!isPermissionRequestArguments(pendingPrompt.arguments)) return []
  if (pendingPrompt.arguments.kind !== 'mcp') return []
  const {serverName, toolName} = pendingPrompt.arguments
  if (!serverName || !toolName) return []

  const respObj = response as {approved?: unknown; scope?: unknown} | null
  if (!respObj || respObj.approved !== true || respObj.scope !== 'session') return []

  const targets: Array<{toolCallId: string}> = []
  for (const entry of logs) {
    const prompt = extractPromptFromEntry(entry)
    if (!prompt) continue
    if (prompt.toolCallId === triggeringToolCallId) continue
    if (prompt.toolName !== 'permission_request') continue
    if (!isPermissionRequestArguments(prompt.arguments)) continue
    if (prompt.arguments.kind !== 'mcp') continue
    if (prompt.arguments.serverName !== serverName) continue
    if (prompt.arguments.toolName !== toolName) continue
    targets.push({toolCallId: prompt.toolCallId})
  }
  return targets
}
